"use client";

import { Brain, Heart, Moon, Wind } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhyClientsTrustAmerico() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const features = [{
    icon: Heart,
    title: "Unwavering Integrity",
    description: "Americo Moscariello leads every project with a commitment to honesty and transparency, ensuring that your vision is built on a foundation of trust and ethical craftsmanship.",
    citation: "Moscariello Construction Core Value"
  }, {
    icon: Moon,
    title: "Precision Engineering",
    description: "From structural integrity to the finest finish, our team employs rigorous technical standards to ensure every build meets the highest quality benchmarks in the industry.",
    citation: "Quality Assurance Protocol"
  }, {
    icon: Wind,
    title: "Seamless Stewardship",
    description: "We don't just build; we manage. Our property stewardship services ensure your investment remains in pristine condition, providing peace of mind for years to come.",
    citation: "Property Management Excellence"
  }, {
    icon: Brain,
    title: "Decades of Expertise",
    description: "With a deep understanding of local architecture and construction nuances, we bring seasoned problem-solving skills to every complex renovation or new build.",
    citation: "Americo Moscariello, Founder"
  }];
  return <section data-section-id="991" id="why-clients-trust-americo" className="bg-background text-foreground relative overflow-hidden py-24 md:py-40 px-4 sm:px-6 lg:px-8 dark">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-32 h-[520px] w-[520px] rounded-lg opacity-30 blur-3xl" style={{
      background: "radial-gradient(circle at 30% 30%, hsl(38 95% 55% / 0.2), transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-6xl">
        {}
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
            The Moscariello Standard
          </p>
          <h2 className="font-serif font-default mt-6 text-4xl sm:text-5xl md:text-6xl font-light leading-[1.05] tracking-tight text-foreground">Why clients trust Moscariello Construction</h2>
          <p className="mt-8 mx-auto max-w-[600px] text-base md:text-lg leading-relaxed text-muted-foreground font-default">
            Building is more than just materials and labor; it is a promise of quality. 
            At Moscariello Construction, we combine decades of technical expertise with 
            a personal commitment to every client we serve.
          </p>
        </div>

        {}
        <div className="mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          return <FeatureCard key={i} data-index={i} index={i} Icon={Icon} title={feature.title} description={feature.description} citation={feature.citation} />;
        })}
        </div>
      </div>
    </section>;
}
function FeatureCard({
  index,
  Icon,
  title,
  description,
  citation,
  ...rest
}: {
  index: number;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  description: string;
  citation: string;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} {...rest} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 120}ms`
  }}>
      <Card className="group h-full bg-card text-card-foreground rounded-lg border border-border p-8 md:p-12 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-500 ease-out">
        <div className="flex flex-col gap-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent">
            <Icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
          </div>

          <h3 className="font-serif font-default text-2xl md:text-[28px] font-light leading-tight text-foreground">
            {title}
          </h3>

          <p className="text-sm md:text-[15px] leading-relaxed text-muted-foreground font-default">
            {description}
          </p>

          <div className="pt-4 mt-auto border-t border-border">
            <p className="font-mono italic text-xs md:text-sm text-muted-foreground/80">
              — {citation}
            </p>
          </div>
        </div>
      </Card>
    </div>;
}